import { useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  MdEdit, MdSave, MdAddPhotoAlternate, MdDelete,
  MdLanguage, MdPhone, MdEmail, MdLocationOn,
  MdFacebook, MdLink, MdAccessTime,
  MdOpenInNew, MdContentCopy, MdCheck, MdVisibility, MdVisibilityOff,
  MdPalette, MdImage, MdArrowForward, MdCheckCircle, MdChevronRight,
} from 'react-icons/md';
import { FaInstagram, FaWhatsapp, FaDumbbell } from 'react-icons/fa';
import useImpersonation from '../../hooks/useImpersonation';
import useClubVocab from '../../hooks/useClubVocab';
import useTheme from '../../context/ThemeContext';
import { getMyClub, updateMyClub, uploadClubImage } from '../../services/clubService';
import { getCommandCenter } from '../../services/adminService';
import { getTrainers } from '../../services/trainerService';
import { getSettings } from '../../services/settingsService';
import { isRealGps } from '../../utils/publicProfile';
import { TRIAL_ENABLED } from '../../config/features';
import { sanitizePhone, isValidPhone, sanitizeEmail, isValidEmail, PHONE_HINT, EMAIL_HINT } from '../../utils/inputRules';

const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const DAY_LABELS = { monday:'Mon', tuesday:'Tue', wednesday:'Wed', thursday:'Thu', friday:'Fri', saturday:'Sat', sunday:'Sun' };

const INPUT_CLS = 'w-full border border-divider rounded-xl px-3 py-2.5 text-sm bg-card text-secondary focus:outline-none focus:border-secondary transition-colors';
const LABEL_CLS = 'text-xs font-medium text-muted mb-1 block';

// Sticky section nav (Stripe-settings feel) — links to the existing anchor ids.
const SECTION_NAV = [
  { id: 'section-branding', label: 'Branding',     Icon: MdImage },
  { id: 'section-info',     label: 'Club Info',    Icon: MdEdit },
  { id: 'section-contact',  label: 'Contact',      Icon: MdPhone },
  { id: 'section-social',   label: 'Social Links', Icon: MdLink },
  { id: 'section-gallery',  label: 'Gallery',      Icon: MdAddPhotoAlternate },
  { id: 'section-hours',    label: 'Hours',        Icon: MdAccessTime },
];

// ── Image upload box ───────────────────────────────────────────────────────────
function ImageUploadBox({ label, value, purpose, onUploaded, aspect = 'square', isCircle = false }) {
  const { isReadOnly } = useImpersonation();
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          const res = await uploadClubImage(ev.target.result, purpose);
          onUploaded(res.data.url);
          toast.success('Image uploaded');
        } catch (err) {
          toast.error(err.response?.data?.message || 'Upload failed');
        } finally {
          setUploading(false);
        }
      };
      reader.onerror = () => { toast.error('Failed to read file'); setUploading(false); };
      reader.readAsDataURL(file);
    } catch {
      setUploading(false);
    }
  };

  if (isCircle) {
    return (
      <div className="flex flex-col items-center gap-2">
        <p className={LABEL_CLS}>{label}</p>
        <div
          className={`relative w-24 h-24 rounded-full overflow-hidden border-4 border-divider transition-colors shadow-sm group ${isReadOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary/40'}`}
          onClick={() => { if (isReadOnly) return; inputRef.current?.click(); }}
          title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
        >
          {value ? (
            <img src={value} alt={label} className="w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-nav-hover">
              <MdAddPhotoAlternate className="text-2xl text-subtle" />
            </div>
          )}
          <div className="absolute inset-0 bg-black/40 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <MdImage className="text-white text-xl" />
          </div>
          {uploading && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
        <p className="text-[10px] text-muted">Click to change</p>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </div>
    );
  }

  return (
    <div>
      <p className={LABEL_CLS}>{label}</p>
      <div
        className={`relative border-2 border-dashed border-divider rounded-xl overflow-hidden transition-colors bg-nav-hover
          ${isReadOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary/50'}
          ${aspect === 'wide' ? 'aspect-[3/1]' : 'w-24 h-24'}`}
        onClick={() => { if (isReadOnly) return; inputRef.current?.click(); }}
        title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
      >
        {value ? (
          <img src={value} alt={label} className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5">
            <MdAddPhotoAlternate className="text-3xl text-subtle" />
            <span className="text-[11px] text-muted font-medium">Click to upload</span>
            {aspect === 'wide' && (
              <span className="text-[10px] text-subtle">Recommended: 1200 × 400 px</span>
            )}
          </div>
        )}
        {value && aspect === 'wide' && (
          <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors flex items-center justify-center opacity-100 lg:opacity-0 lg:hover:opacity-100">
            <span className="bg-black/60 text-white text-xs px-3 py-1.5 rounded-full font-medium">Change photo</span>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
    </div>
  );
}

// ── Inline profile preview (uses watched form values) ─────────────────────────
function ProfilePreview({ values, slug, onClose }) {
  const brand = values.primaryColor || '#E24B4A';
  const hasHours = DAYS.some(d => values.operatingHours?.[d]?.trim());
  const hasContact = values.contactPhone || values.contactEmail || values.whatsappNumber;
  const hasSocial = values.facebookUrl || values.instagramUrl || values.websiteUrl;
  const hasAddress = values.address?.street || values.address?.city;

  return (
    <div className="rounded-2xl overflow-hidden border border-divider shadow-sm bg-card">
      {/* Preview banner */}
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center justify-between gap-3">
        <p className="text-xs text-amber-700 font-medium flex items-center gap-1.5">
          <MdVisibility className="text-amber-500" />
          Preview — showing how your public profile looks with current values
        </p>
        <div className="flex items-center gap-3 flex-shrink-0">
          {slug && (
            <a href={`/${slug}`} target="_blank" rel="noreferrer"
              className="text-xs text-amber-700 hover:text-amber-900 font-semibold flex items-center gap-1 hover:underline">
              Open live <MdOpenInNew />
            </a>
          )}
          <button onClick={onClose}
            className="text-xs text-amber-700 hover:text-amber-900 font-semibold">
            ← Back to editor
          </button>
        </div>
      </div>

      {/* Hero */}
      <div className="relative" style={{ backgroundColor: brand, minHeight: 200 }}>
        {values.coverPhotoUrl && (
          <img src={values.coverPhotoUrl} alt="cover"
            className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/65" />
        <div className="relative px-6 pt-8 pb-5">
          <div className="mb-4">
            {values.logo ? (
              <img src={values.logo} alt="logo"
                className="w-16 h-16 rounded-2xl border-4 border-white object-cover shadow-lg" />
            ) : (
              <div className="w-16 h-16 rounded-2xl border-4 border-white flex items-center justify-center shadow-lg"
                style={{ backgroundColor: brand }}>
                <FaDumbbell className="text-white text-xl" />
              </div>
            )}
          </div>
          <p className="font-extrabold text-2xl text-white leading-tight drop-shadow">
            {values.name || 'Your Club Name'}
          </p>
          {values.tagline && (
            <p className="text-sm text-white/80 mt-1">{values.tagline}</p>
          )}
          <div className="flex flex-wrap gap-2 mt-4">
            {TRIAL_ENABLED && (
              <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white shadow"
                style={{ backgroundColor: brand }}>
                🎯 Book a Free Trial
              </span>
            )}
            {values.whatsappNumber && (
              <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white bg-green-600 shadow">
                WhatsApp
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Description */}
      {values.description && (
        <div className="px-6 py-5 border-t border-divider">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1 h-5 rounded-full" style={{ backgroundColor: brand }} />
            <p className="font-semibold text-secondary text-sm">About Us</p>
          </div>
          <p className="text-sm text-muted leading-relaxed">{values.description}</p>
        </div>
      )}

      {/* Why Join (always show) */}
      <div className="px-6 py-5 border-t border-divider bg-nav-hover">
        <p className="font-semibold text-secondary text-sm mb-3 text-center">Why Join Us?</p>
        <div className="grid grid-cols-3 gap-3">
          {['Expert Training', 'All Ages Welcome', 'Convenient Location'].map((t, i) => (
            <div key={i} className="bg-card rounded-xl p-3 text-center border border-divider">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center mx-auto mb-2 text-white text-sm"
                style={{ backgroundColor: brand }}>
                {['🏆','👨‍👩‍👧','📍'][i]}
              </div>
              <p className="text-[10px] font-semibold text-secondary leading-tight">{t}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Operating Hours */}
      {hasHours && (
        <div className="px-6 py-5 border-t border-divider">
          <p className="font-semibold text-secondary text-sm mb-3 flex items-center gap-2">
            <MdAccessTime style={{ color: brand }} /> Operating Hours
          </p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            {DAYS.map(d => {
              const v = values.operatingHours?.[d];
              if (!v?.trim()) return null;
              return (
                <div key={d} className="flex items-center gap-2 text-xs">
                  <span className="text-subtle capitalize w-8">{d.slice(0,3)}</span>
                  <span className={/closed/i.test(v) ? 'text-red-500' : 'text-secondary'}>{v}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Contact */}
      {(hasContact || hasAddress) && (
        <div className="px-6 py-5 border-t border-divider">
          <p className="font-semibold text-secondary text-sm mb-3 flex items-center gap-2">
            <MdPhone style={{ color: brand }} /> Contact
          </p>
          <div className="space-y-1.5">
            {values.contactPhone && (
              <p className="text-xs text-muted flex items-center gap-2">
                <MdPhone className="text-subtle" /> {values.contactPhone}
              </p>
            )}
            {values.contactEmail && (
              <p className="text-xs text-muted flex items-center gap-2">
                <MdEmail className="text-subtle" /> {values.contactEmail}
              </p>
            )}
            {values.whatsappNumber && (
              <p className="text-xs text-green-600 flex items-center gap-2">
                <span>WhatsApp</span> {values.whatsappNumber}
              </p>
            )}
            {hasAddress && (
              <p className="text-xs text-muted flex items-center gap-2">
                <MdLocationOn className="text-subtle" />
                {[values.address?.street, values.address?.city, values.address?.district].filter(Boolean).join(', ')}
              </p>
            )}
          </div>
          {hasSocial && (
            <div className="flex gap-2 mt-3">
              {values.facebookUrl  && <span className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600 text-xs">f</span>}
              {values.instagramUrl && <span className="w-7 h-7 bg-pink-50 rounded-lg flex items-center justify-center text-pink-500 text-xs">ig</span>}
              {values.websiteUrl   && <span className="w-7 h-7 bg-nav-hover rounded-lg flex items-center justify-center text-muted text-xs">🌐</span>}
            </div>
          )}
        </div>
      )}

      {/* CTA (hidden with the trial-student feature) */}
      {TRIAL_ENABLED && (
        <div className="px-6 py-6 text-center border-t border-divider" style={{ backgroundColor: brand }}>
          <p className="text-white font-extrabold text-base mb-1">Ready to Start Your Journey?</p>
          <p className="text-white/75 text-xs mb-3">Join {values.name || 'your club'} today</p>
          <span className="inline-block bg-card rounded-xl px-6 py-2.5 text-xs font-bold shadow"
            style={{ color: brand }}>
            Register for Free Trial →
          </span>
        </div>
      )}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────
function relTime(ts) {
  const diff  = Date.now() - new Date(ts).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const LEAD_STATUS = {
  pending:   { label: 'New Request',      cls: 'bg-amber-500/15 text-amber-400 border border-amber-500/20' },
  approved:  { label: 'Contacted',        cls: 'bg-blue-100   text-blue-700'    },
  attended:  { label: 'Contacted',        cls: 'bg-blue-100   text-blue-700'    },
  follow_up: { label: 'Contacted',        cls: 'bg-blue-100   text-blue-700'    },
  invited:   { label: 'Contacted',        cls: 'bg-blue-100   text-blue-700'    },
  converted: { label: 'Added as Student', cls: 'bg-purple-100 text-purple-700'  },
  rejected:  { label: 'Not Joined',       cls: 'bg-gray-100   text-gray-400'    },
};

function LeadStatusPill({ status }) {
  const v = useClubVocab();
  const s = LEAD_STATUS[status] || { label: status || 'New', cls: 'bg-gray-100 text-gray-400' };
  return (
    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${s.cls}`}>{s.label.replace(/\bStudent\b/g, v.member)}</span>
  );
}

// ── Website Enquiries card ─────────────────────────────────────────────────────
function WebsiteEnquiriesCard({ shareUrl, slug, wl, isLoading }) {
  const currentTheme = useTheme()?.currentTheme;
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const stats = [
    { label: 'This Month',  value: wl.thisMonth    || 0, color: 'text-blue-600',                                                          bg: 'bg-blue-50'   },
    { label: 'Last 7 Days', value: wl.last7Days    || 0, color: 'text-indigo-600',                                                        bg: 'bg-indigo-50' },
    { label: 'Pending',     value: wl.pendingCount || 0, color: (wl.pendingCount || 0) > 0 ? 'text-amber-600' : 'text-subtle',          bg: (wl.pendingCount || 0) > 0 ? 'bg-amber-50' : 'bg-nav-hover' },
  ];

  return (
    <div className="bg-card border border-divider rounded-2xl p-5 shadow-sm space-y-4">

      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-secondary text-sm flex items-center gap-2">
            <MdLanguage className="text-primary" /> Public Website Performance
          </h2>
          <p className="text-xs text-subtle mt-0.5">Track website requests coming from your public club website.</p>
        </div>
        <Link
          to="/admin/trial-students?source=public_website"
          className="text-xs font-bold text-primary hover:underline flex items-center gap-0.5 whitespace-nowrap flex-shrink-0 mt-0.5">
          View Website Requests <MdArrowForward size={13} />
        </Link>
      </div>

      {/* Stats row */}
      {isLoading ? (
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3].map(i => <div key={i} className="h-14 bg-nav-hover rounded-xl animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {stats.map(s => (
            <div key={s.label} className={`${s.bg} rounded-xl p-3 text-center`}>
              <p className={`text-xl font-bold leading-none ${s.color}`}>{s.value}</p>
              <p className="text-[11px] text-subtle mt-1 leading-tight">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Latest enquiries or empty state */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2].map(i => <div key={i} className="h-10 bg-nav-hover rounded-lg animate-pulse" />)}
        </div>
      ) : (wl.latest || []).length > 0 ? (
        <div>
          <p className="text-[11px] font-bold text-subtle uppercase tracking-widest mb-2">Latest Website Requests</p>
          <div className="divide-y divide-divider">
            {wl.latest.map((lead, i) => (
              <div key={i} className="flex items-center gap-2.5 py-2.5">
                <div className="w-7 h-7 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {(lead.name || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-secondary truncate leading-tight">{lead.name}</p>
                  <p className="text-xs text-subtle">{lead.phone}</p>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className="text-[11px] text-subtle">{relTime(lead.createdAt)}</span>
                  <LeadStatusPill status={lead.status} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-nav-hover rounded-xl p-4 text-center">
          <p className="text-sm font-medium text-muted">No website requests yet</p>
          <p className="text-xs text-subtle mt-1 leading-snug max-w-xs mx-auto">
            Share your public club website or use the Book Trial button to start receiving requests.
          </p>
        </div>
      )}

      {/* Website URL + action buttons */}
      {shareUrl && (
        <div className="pt-3 border-t border-divider space-y-2">
          <div className="flex items-center gap-2 bg-nav-hover rounded-xl px-3 py-2 border border-divider">
            <MdLanguage className="text-subtle text-sm flex-shrink-0" />
            <span className="text-xs text-muted font-medium truncate flex-1">{shareUrl}</span>
            <button
              type="button"
              onClick={handleCopy}
              className="flex items-center gap-1 text-xs text-primary font-medium hover:opacity-75 transition-opacity flex-shrink-0">
              {copied
                ? <><MdCheck className="text-green-500" /><span className="text-green-600">Copied!</span></>
                : <><MdContentCopy /><span>Copy</span></>}
            </button>
          </div>
          <div className="flex gap-2">
            <a
              href={`/${slug}`}
              target="_blank"
              rel="noreferrer"
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-xs font-semibold bg-card text-secondary border border-divider hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200">
              <MdOpenInNew className="text-sm" /> Open Website
            </a>
            <Link
              to="/admin/trial-students?source=public_website"
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-xs font-semibold ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} hover:opacity-90 transition-all duration-200`}>
              View Website Requests
            </Link>
          </div>
        </div>
      )}

      {/* Feature summary helper */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-3.5">
        <p className="text-[11px] font-bold text-blue-700 uppercase tracking-wide mb-2">What visitors can do on your website</p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1">
          {['Learn about your club', 'View coaches and location', 'Book a free trial', 'Contact you by WhatsApp or phone'].map(item => (
            <li key={item} className="text-xs text-blue-600 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
        {slug && (
          <a href={`/${slug}`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 mt-3 text-xs font-bold text-blue-700 hover:underline">
            Preview Public Website <MdOpenInNew className="text-xs" />
          </a>
        )}
      </div>
    </div>
  );
}

// ── Card wrapper ───────────────────────────────────────────────────────────────
function Card({ icon, title, children, id, hidden }) {
  return (
    <div id={id} className={`bg-card border border-divider rounded-2xl p-6 shadow-sm space-y-4 ${hidden ? 'hidden' : ''}`}>
      <h2 className="font-semibold text-secondary flex items-center gap-2">
        {icon} {title}
      </h2>
      {children}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function ClubProfile() {
  const { isReadOnly } = useImpersonation();
  const queryClient    = useQueryClient();
  const v              = useClubVocab();
  const galleryInputRef = useRef();
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [previewMode, setPreviewMode]           = useState(false);
  const [linkCopied, setLinkCopied]             = useState(false);
  const [checklistOpen, setChecklistOpen]       = useState(null); // null = follow default (open <50%)
  const [searchParams, setSearchParams]         = useSearchParams();
  const [activeSection, setActiveSection]       = useState(() => {
    const p  = searchParams.get('section');
    const id = p ? `section-${p}` : null;
    return SECTION_NAV.some(s => s.id === id) ? id : 'section-branding';
  });

  // Switch tab + sync ?section= to the URL (replace, so the tab survives a refresh).
  const changeSection = (id) => {
    setActiveSection(id);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('section', id.replace('section-', ''));
      return next;
    }, { replace: true });
  };

  const { data, isLoading } = useQuery({
    queryKey: ['my-club'],
    queryFn:  () => getMyClub().then(r => r.data.club),
  });

  const { data: ccData, isLoading: ccLoading } = useQuery({
    queryKey: ['command-center'],
    queryFn:  () => getCommandCenter().then(r => r.data),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Coaches (for the completeness checklist). If unavailable, coach items are skipped.
  // refetchOnMount:'always' so returning after adding a coach on the Trainers page reflects
  // the true count (the checklist read stale otherwise).
  const { data: trainersData } = useQuery({
    queryKey: ['trainers'],
    queryFn:  () => getTrainers({ limit: 100, isActive: 'true' }).then(r => r.data.trainers),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: false,
  });

  // yearEstablished lives in Settings (not the club save path) — read-only here.
  // refetchOnMount:'always' so the 'Set your founding year' check reflects a value just saved
  // on the Settings page instead of a 5-minute-stale cache.
  const { data: settingsData } = useQuery({
    queryKey: ['settings'],
    queryFn:  () => getSettings().then(r => r.data.settings),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: false,
  });

  const { register, handleSubmit, watch, setValue, formState: { isDirty, isSubmitting, errors } } = useForm({
    defaultValues: {
      name: '', displayName: '', tagline: '', description: '',
      contactEmail: '', contactPhone: '', whatsappNumber: '',
      facebookUrl: '', instagramUrl: '', websiteUrl: '',
      primaryColor: '#E24B4A',
      address: { street: '', city: '', district: '', country: 'Nepal' },
      operatingHours: { monday:'', tuesday:'', wednesday:'', thursday:'', friday:'', saturday:'', sunday:'' },
      logo: '', coverPhotoUrl: '', galleryPhotos: [],
    },
    values: data ? {
      name:           data.name          || '',
      displayName:    data.displayName    || '',
      tagline:        data.tagline        || '',
      description:    data.description    || '',
      contactEmail:   data.contactEmail   || '',
      contactPhone:   data.contactPhone   || '',
      whatsappNumber: data.whatsappNumber || '',
      facebookUrl:    data.facebookUrl    || '',
      instagramUrl:   data.instagramUrl   || '',
      websiteUrl:     data.websiteUrl     || '',
      primaryColor:   data.primaryColor   || '#E24B4A',
      address:        data.address        || { street:'', city:'', district:'', country:'Nepal' },
      operatingHours: data.operatingHours || {},
      logo:           data.logo           || '',
      coverPhotoUrl:  data.coverPhotoUrl  || '',
      galleryPhotos:  data.galleryPhotos  || [],
    } : undefined,
  });

  const watchedValues  = watch();
  const logo           = watchedValues.logo;
  const coverPhotoUrl  = watchedValues.coverPhotoUrl;
  const galleryPhotos  = watchedValues.galleryPhotos || [];
  const primaryColor   = watchedValues.primaryColor;
  const name           = watchedValues.name;
  const displayName    = watchedValues.displayName;
  const tagline        = watchedValues.tagline;
  const slug           = data?.slug;

  const shareUrl = slug ? `${window.location.origin}/${slug}` : null;

  const copyLink = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  const saveMutation = useMutation({
    mutationFn: (formData) => updateMyClub(formData),
    onSuccess: () => {
      toast.success('Club profile saved!');
      queryClient.invalidateQueries({ queryKey: ['my-club'] });
      queryClient.invalidateQueries({ queryKey: ['tenant'] });
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Save failed'),
  });

  const onSubmit = (formData) => saveMutation.mutate(formData);

  const handleGalleryUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    if (galleryPhotos.length >= 6)   { toast.error('Maximum 6 gallery photos'); return; }
    setGalleryUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          const res = await uploadClubImage(ev.target.result, 'gallery');
          setValue('galleryPhotos', [...galleryPhotos, res.data.url], { shouldDirty: true });
        } catch (err) {
          toast.error(err.response?.data?.message || 'Upload failed');
        } finally {
          setGalleryUploading(false);
        }
      };
      reader.onerror = () => { toast.error('Failed to read file'); setGalleryUploading(false); };
      reader.readAsDataURL(file);
    } catch {
      setGalleryUploading(false);
    }
  };

  const removeGalleryPhoto = (idx) =>
    setValue('galleryPhotos', galleryPhotos.filter((_, i) => i !== idx), { shouldDirty: true });

  // ── Profile completeness checklist (live from watched values) ──────────────
  const description    = watchedValues.description || '';
  const contactPhone   = watchedValues.contactPhone || '';
  const operatingHours = watchedValues.operatingHours || {};
  const hasSocial      = !!(watchedValues.facebookUrl || watchedValues.instagramUrl || watchedValues.websiteUrl);
  const descLen        = description.trim().length;
  const galleryCount   = galleryPhotos.length;
  const filledDays     = DAYS.filter(d => (operatingHours[d] || '').trim()).length;

  // Each item reads the actually-saved data (watchedValues are RHF-synced from the my-club
  // fetch; settings/trainers queries refetch on mount so they aren't stale). Only checks that
  // can be judged accurately are included — see the audit note below.
  //   REMOVED 'brand': `primaryColor !== '#E24B4A'` can't tell a deliberate default from an
  //     unset one, so it read false-incomplete for every club using the default red — a major
  //     reason the card "never completed".
  //   FIXED 'hours': required all 7 days (filledDays === 7), impossible for clubs closed on
  //     some days → now "most of the week" (≥5).
  //   CONSOLIDATED coaches to a single reliable "has ≥1 coach" check; the per-coach bio/photo
  //     nags were noisy, not actionable here, and prone to staleness.
  const checklist = [
    { key: 'logo',    done: !!logo,                label: 'Add a club logo',    section: 'section-branding' },
    { key: 'cover',   done: !!coverPhotoUrl,       label: 'Add a cover photo',  section: 'section-branding' },
    { key: 'desc',    done: descLen >= 100,        label: descLen === 0 ? 'Write an “About” description' : 'Your description is short — aim for 100+ characters', section: 'section-info' },
    { key: 'phone',   done: !!contactPhone.trim(), label: 'Add a contact phone number', section: 'section-contact' },
    { key: 'social',  done: hasSocial,             label: 'Add a social or website link', section: 'section-social' },
    { key: 'gallery', done: galleryCount >= 3,     label: galleryCount === 0 ? 'Add photos to your gallery' : `Add ${Math.max(0, 3 - galleryCount)} more gallery photo${3 - galleryCount === 1 ? '' : 's'}`, section: 'section-gallery' },
    { key: 'hours',   done: filledDays >= 5,       label: filledDays === 0 ? 'Add your opening hours' : 'Add hours for the rest of your week', section: 'section-hours' },
  ];

  // yearEstablished is owned by the Settings page — score it, link there.
  if (settingsData !== undefined) {
    checklist.push({ key: 'year', done: !!settingsData.yearEstablished, label: 'Set your founding year', to: '/admin/settings' });
  }

  // Coaches — single accurate check: has at least one active coach (managed on Trainers).
  if (Array.isArray(trainersData)) {
    checklist.push({ key: 'coach', done: trainersData.length > 0, label: 'Add your first coach (managed in Trainers)', static: true });
  }

  const totalItems     = checklist.length;
  const completedItems = checklist.filter(i => i.done).length;
  const completeness   = totalItems ? Math.round((completedItems / totalItems) * 100) : 100;
  const pendingItems   = checklist.filter(i => !i.done);
  const checklistExpanded = checklistOpen === null ? completeness < 50 : checklistOpen;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto pb-24">

      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div className="bg-card border border-divider rounded-2xl p-5 shadow-sm mb-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-base font-bold text-secondary">Public Club Profile</h1>
            <p className="text-xs text-muted mt-0.5">This is what parents and {v.members.toLowerCase()} see when they visit your club link.</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => setPreviewMode(p => !p)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-colors
                ${previewMode
                  ? 'border-secondary text-secondary hover:bg-nav-hover'
                  : 'border-divider text-muted hover:bg-nav-hover hover:text-secondary'}`}
            >
              {previewMode ? <MdVisibilityOff className="text-sm" /> : <MdVisibility className="text-sm" />}
              {previewMode ? 'Hide Preview' : 'Preview'}
            </button>
            {slug && (
              <a href={`/${slug}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border border-divider text-muted hover:bg-nav-hover hover:text-secondary transition-colors">
                <MdOpenInNew className="text-sm" /> Live Page
              </a>
            )}
          </div>
        </div>

        {shareUrl && (
          <div className="mt-3 flex items-center gap-2 bg-nav-hover rounded-xl px-3 py-2 border border-divider">
            <span className="text-xs text-muted flex-shrink-0">Share at:</span>
            <span className="text-xs text-secondary font-medium truncate flex-1">{shareUrl}</span>
            <button onClick={copyLink}
              className="flex items-center gap-1 text-xs text-primary font-medium hover:opacity-75 transition-opacity flex-shrink-0">
              {linkCopied ? <><MdCheck className="text-emerald-400" /><span className="text-emerald-400">Copied!</span></> : <><MdContentCopy /><span>Copy</span></>}
            </button>
          </div>
        )}
      </div>

      {previewMode ? (
        /* ── Preview mode (full width, no side nav) ─────────────────────── */
        <ProfilePreview values={watchedValues} slug={slug} onClose={() => setPreviewMode(false)} />
      ) : (
        /* ── Edit mode: sticky section nav + content column ─────────────── */
        <div className="lg:flex lg:gap-7 lg:items-start">

          {/* Desktop sticky section nav */}
          <aside className="hidden lg:block w-52 flex-shrink-0">
            <nav className="sticky top-20 space-y-0.5">
              {SECTION_NAV.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => changeSection(id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-colors text-left ${
                    activeSection === id
                      ? 'bg-nav-hover text-primary'
                      : 'text-muted hover:bg-nav-hover hover:text-secondary'
                  }`}
                >
                  <Icon size={16} className="flex-shrink-0" />
                  <span className="truncate">{label}</span>
                </button>
              ))}
            </nav>
          </aside>

          {/* Content column */}
          <div className="flex-1 min-w-0 space-y-5">

            {/* Mobile sticky section chips */}
            <div className="lg:hidden sticky top-16 z-20 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2 bg-background border-b border-divider">
              <div className="flex gap-1.5 overflow-x-auto pb-0.5">
                {SECTION_NAV.map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => changeSection(id)}
                    className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border transition-colors ${
                      activeSection === id
                        ? 'bg-primary text-white border-primary'
                        : 'bg-card text-muted border-divider hover:bg-nav-hover'
                    }`}
                  >
                    <Icon size={13} className="flex-shrink-0" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Completion checklist — compact: one row (bar + count + chevron), expandable */}
            {completeness >= 100 ? (
              <div className="bg-card border border-divider rounded-2xl px-4 py-3 flex items-center gap-2">
                <MdCheckCircle className="text-emerald-400 text-lg flex-shrink-0" />
                <span className="text-sm font-medium text-secondary">Profile complete</span>
              </div>
            ) : (
              <div className="bg-card border border-divider rounded-2xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setChecklistOpen(!checklistExpanded)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-nav-hover transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-xs font-semibold text-secondary">Complete your public profile</span>
                      <span className="text-xs text-muted flex-shrink-0">{completedItems} of {totalItems} complete</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-nav-hover overflow-hidden">
                      <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${completeness}%` }} />
                    </div>
                  </div>
                  <MdChevronRight className={`text-muted text-lg flex-shrink-0 transition-transform duration-200 ${checklistExpanded ? 'rotate-90' : ''}`} />
                </button>

                {checklistExpanded && (
                  <div className="px-3 pb-3 pt-1 space-y-1.5">
                    {pendingItems.map(item => {
                      const rowCls = `w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-left bg-amber-500/10 transition-colors ${item.static ? '' : 'hover:bg-amber-500/15 cursor-pointer'}`;
                      const inner = (
                        <>
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                          <span className="text-xs text-secondary flex-1 min-w-0">{item.label}</span>
                          {!item.static && <MdArrowForward className="text-amber-400/70 text-sm flex-shrink-0" />}
                        </>
                      );
                      if (item.to)     return <Link key={item.key} to={item.to} className={rowCls}>{inner}</Link>;
                      if (item.static) return <div key={item.key} className={rowCls}>{inner}</div>;
                      return <button key={item.key} type="button" onClick={() => changeSection(item.section)} className={rowCls}>{inner}</button>;
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Website Enquiries (hidden with the trial-student feature) */}
            {TRIAL_ENABLED && (
              <WebsiteEnquiriesCard
                shareUrl={shareUrl}
                slug={slug}
                wl={ccData?.websiteLeads || {}}
                isLoading={ccLoading}
              />
            )}

            {/* Edit form — all six sections stay mounted (RHF fields must not unmount);
                inactive tabs are hidden with `hidden`, preserving their values on submit. */}
            <form onSubmit={handleSubmit(onSubmit)}>

              {/* Cover + Logo + Brand */}
              <div id="section-branding" className={`bg-card border border-divider rounded-2xl overflow-hidden shadow-sm ${activeSection === 'section-branding' ? '' : 'hidden'}`}>
                <div className="relative">
                  <ImageUploadBox
                    label="Cover Photo"
                    value={coverPhotoUrl}
                    purpose="cover"
                    aspect="wide"
                    onUploaded={(url) => setValue('coverPhotoUrl', url, { shouldDirty: true })}
                  />
                  {/* Logo overlapping bottom of cover */}
                  <div className="absolute left-6 -bottom-10 z-10">
                    <ImageUploadBox
                      label=""
                      value={logo}
                      purpose="logo"
                      isCircle
                      onUploaded={(url) => setValue('logo', url, { shouldDirty: true })}
                    />
                  </div>
                </div>

                <div className="px-6 pb-5 pt-14 flex items-center justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-bold text-secondary text-base truncate">{name || 'Your Club Name'}</p>
                    <p className="text-sm text-muted truncate">{tagline || 'Add a tagline below'}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <label className="text-xs text-muted flex items-center gap-1">
                      <MdPalette className="text-muted" /> Brand Color
                    </label>
                    <input
                      type="color"
                      {...register('primaryColor')}
                      className="w-9 h-9 rounded-xl cursor-pointer border border-divider p-0.5 bg-card"
                      title="Brand color — used on buttons and accents"
                    />
                    <span className="text-xs text-muted font-mono">{primaryColor}</span>
                  </div>
                </div>

                {/* Display Name — short name for menu / small spaces */}
                <div className="px-6 pb-6 pt-1">
                  <div className="flex items-center justify-between">
                    <label className={LABEL_CLS}>Display Name <span className="text-muted font-normal">(optional)</span></label>
                    <span className={`text-[11px] font-mono ${(displayName || '').length > 25 ? 'text-red-400' : 'text-muted'}`}>{(displayName || '').length}/25</span>
                  </div>
                  <input
                    {...register('displayName', { maxLength: 25 })}
                    maxLength={25}
                    placeholder={name || 'e.g. Master Sonam TKD'}
                    className={INPUT_CLS}
                  />
                  <p className="text-xs text-muted mt-1.5">
                    Short name shown in the website menu and small spaces. Your full club name still appears on the page heading.
                  </p>
                </div>
              </div>

              {/* Club Information */}
              <Card id="section-info" icon={<MdEdit className="text-primary" />} title="Club Information" hidden={activeSection !== 'section-info'}>
                <div>
                  <label className={LABEL_CLS}>Club Name *</label>
                  <input {...register('name', { required: true })} className={INPUT_CLS} />
                </div>
                <div>
                  <label className={LABEL_CLS}>
                    Tagline
                    <span className="text-muted font-normal ml-1">(max 100 chars — displayed on your public profile)</span>
                  </label>
                  <input
                    {...register('tagline', { maxLength: 100 })}
                    placeholder="e.g. Train hard. Achieve more."
                    className={INPUT_CLS}
                  />
                </div>
                <div>
                  <label className={LABEL_CLS}>
                    Description
                    <span className="text-muted font-normal ml-1">(max 500 chars)</span>
                  </label>
                  <textarea
                    {...register('description', { maxLength: 500 })}
                    rows={4}
                    placeholder="Tell prospective members about your club, coaches, and programs..."
                    className={`${INPUT_CLS} resize-none`}
                  />
                </div>
              </Card>

              {/* Contact */}
              <Card id="section-contact" icon={<MdPhone className="text-primary" />} title="Contact Details" hidden={activeSection !== 'section-contact'}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={`${LABEL_CLS} flex items-center gap-1`}>
                      <MdEmail className="text-muted" /> Contact Email
                    </label>
                    <input {...register('contactEmail', { validate: (v) => isValidEmail(v) || EMAIL_HINT })}
                      onChange={(e) => setValue('contactEmail', sanitizeEmail(e.target.value), { shouldValidate: true, shouldDirty: true })}
                      type="email" className={`${INPUT_CLS} ${errors.contactEmail ? '!border-red-400' : ''}`} />
                    {errors.contactEmail && <p className="text-xs text-red-500 mt-1">{EMAIL_HINT}</p>}
                  </div>
                  <div>
                    <label className={`${LABEL_CLS} flex items-center gap-1`}>
                      <MdPhone className="text-muted" /> Phone Number
                    </label>
                    <input {...register('contactPhone', { validate: (v) => isValidPhone(v) || PHONE_HINT })}
                      onChange={(e) => setValue('contactPhone', sanitizePhone(e.target.value), { shouldValidate: true, shouldDirty: true })}
                      className={`${INPUT_CLS} ${errors.contactPhone ? '!border-red-400' : ''}`} />
                    {errors.contactPhone && <p className="text-xs text-red-500 mt-1">{PHONE_HINT}</p>}
                  </div>
                  <div>
                    <label className={`${LABEL_CLS} flex items-center gap-1`}>
                      <FaWhatsapp className="text-green-500" /> WhatsApp Number
                    </label>
                    <input {...register('whatsappNumber', { validate: (v) => isValidPhone(v) || PHONE_HINT })}
                      onChange={(e) => setValue('whatsappNumber', sanitizePhone(e.target.value), { shouldValidate: true, shouldDirty: true })}
                      placeholder="+977-98XXXXXXXX" className={`${INPUT_CLS} ${errors.whatsappNumber ? '!border-red-400' : ''}`} />
                    {errors.whatsappNumber && <p className="text-xs text-red-500 mt-1">{PHONE_HINT}</p>}
                  </div>
                </div>

                <div className="pt-2 border-t border-divider">
                  <p className="text-xs font-semibold text-muted mb-3 flex items-center gap-1">
                    <MdLocationOn className="text-subtle" /> Address
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={LABEL_CLS}>Street</label>
                      <input {...register('address.street')} className={INPUT_CLS} />
                    </div>
                    <div>
                      <label className={LABEL_CLS}>City</label>
                      <input {...register('address.city')} className={INPUT_CLS} />
                    </div>
                    <div>
                      <label className={LABEL_CLS}>District</label>
                      <input {...register('address.district')} className={INPUT_CLS} />
                    </div>
                  </div>
                  {/* GPS nudge — only when the club's map pin isn't really set (still the
                      Kathmandu default), so parents don't get vague/wrong directions. */}
                  {settingsData !== undefined && !isRealGps(settingsData?.gps) && (
                    <p className="text-xs text-muted mt-3 flex items-start gap-1.5">
                      <MdLocationOn className="text-subtle flex-shrink-0 mt-0.5" />
                      <span>
                        Set your exact location in{' '}
                        <Link to="/admin/settings" className="text-primary hover:underline font-medium">Settings → Location</Link>{' '}
                        so parents get precise directions.
                      </span>
                    </p>
                  )}
                </div>
              </Card>

              {/* Social & Web */}
              <Card id="section-social" icon={<MdLink className="text-primary" />} title="Social & Web Links" hidden={activeSection !== 'section-social'}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={`${LABEL_CLS} flex items-center gap-1`}>
                      <MdFacebook className="text-blue-600" /> Facebook URL
                    </label>
                    <input {...register('facebookUrl')} placeholder="https://facebook.com/yourpage" className={INPUT_CLS} />
                  </div>
                  <div>
                    <label className={`${LABEL_CLS} flex items-center gap-1`}>
                      <FaInstagram className="text-pink-500" /> Instagram URL
                    </label>
                    <input {...register('instagramUrl')} placeholder="https://instagram.com/yourhandle" className={INPUT_CLS} />
                  </div>
                  <div>
                    <label className={`${LABEL_CLS} flex items-center gap-1`}>
                      <MdLanguage className="text-muted" /> Website URL
                    </label>
                    <input {...register('websiteUrl')} placeholder="https://yourclub.com" className={INPUT_CLS} />
                  </div>
                </div>
              </Card>

              {/* Gallery */}
              <Card id="section-gallery" icon={<MdAddPhotoAlternate className="text-primary" />} title={`Gallery Photos (${galleryPhotos.length}/6)`} hidden={activeSection !== 'section-gallery'}>
                <div className="flex items-center justify-between -mt-2">
                  <p className="text-xs text-muted">Add up to 6 photos of your facility, classes, or events.</p>
                  {galleryPhotos.length < 6 && (
                    <button type="button" onClick={() => galleryInputRef.current?.click()}
                      disabled={isReadOnly}
                      title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                      className="text-xs text-primary font-medium hover:opacity-75 transition-opacity flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed">
                      {galleryUploading
                        ? <span className="w-3 h-3 border border-primary border-t-transparent rounded-full animate-spin inline-block" />
                        : <MdAddPhotoAlternate />}
                      Add Photo
                    </button>
                  )}
                </div>
                <input ref={galleryInputRef} type="file" accept="image/*" className="hidden" onChange={handleGalleryUpload} />
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {galleryPhotos.map((url, i) => (
                    <div key={i} className="relative aspect-square rounded-xl overflow-hidden group">
                      <img src={url} alt={`Gallery ${i+1}`} className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeGalleryPhoto(i)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity shadow-sm">
                        <MdDelete className="text-xs" />
                      </button>
                    </div>
                  ))}
                  {galleryPhotos.length === 0 && (
                    <button type="button" onClick={() => galleryInputRef.current?.click()}
                      disabled={isReadOnly}
                      title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                      className="col-span-3 sm:col-span-6 border-2 border-dashed border-divider rounded-xl py-8 text-center hover:border-primary/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      <MdAddPhotoAlternate className="text-3xl text-subtle mx-auto mb-1" />
                      <p className="text-xs text-muted">Click to add gallery photos</p>
                    </button>
                  )}
                </div>
              </Card>

              {/* Operating Hours */}
              <Card id="section-hours" icon={<MdAccessTime className="text-primary" />} title="Operating Hours" hidden={activeSection !== 'section-hours'}>
                <p className="text-xs text-muted -mt-2">
                  Enter times like "6:00 AM – 9:00 PM" or "Closed".
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {DAYS.map(day => (
                    <div key={day} className="flex items-center gap-3">
                      <span className="text-xs font-semibold text-muted w-8 flex-shrink-0">{DAY_LABELS[day]}</span>
                      <input
                        {...register(`operatingHours.${day}`)}
                        placeholder="e.g. 6:00 AM – 9:00 PM"
                        className={`${INPUT_CLS} flex-1`}
                      />
                    </div>
                  ))}
                </div>
              </Card>

              {/* Sticky save bar (spans all tabs — fields stay mounted) */}
              <div className="sticky bottom-16 md:bottom-0 z-20 mt-5 -mx-4 sm:-mx-6 lg:mx-0 px-4 sm:px-6 lg:px-4 py-3 bg-card/95 backdrop-blur border-t border-divider flex items-center justify-between gap-3">
                <p className="text-xs text-muted">
                  {isDirty ? '● Unsaved changes' : 'All changes saved'}
                </p>
                <button
                  type="submit"
                  disabled={!isDirty || isSubmitting || saveMutation.isPending || isReadOnly}
                  title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                  className="flex items-center gap-2 px-6 py-2.5 text-white rounded-full text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: primaryColor || '#E24B4A' }}
                >
                  <MdSave className="text-lg" />
                  {saveMutation.isPending ? 'Saving…' : 'Save Profile'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
